/**
 * Utilidades de validación
 * Funciones para validar emails, RUC, números de cheque, etc.
 */

/**
 * Validar email
 * @param {string} email - Email a validar
 * @returns {boolean} true si es válido
 */
export const isValidEmail = (email) => {
  if (!email) return false;

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validar RUC/RIF venezolano
 * Formato: J-12345678-9 o J123456789
 * @param {string} ruc - RUC a validar
 * @returns {boolean} true si es válido
 */
export const isValidRUC = (ruc) => {
  if (!ruc) return false;

  // Remover guiones y espacios
  const cleaned = ruc.replace(/[-\s]/g, '');

  // Formato: Letra + 8 o 9 dígitos + dígito verificador
  const rucRegex = /^[VJEGCP][0-9]{8,9}$/i;

  if (!rucRegex.test(cleaned)) {
    return false;
  }

  return true;
};

/**
 * Validar CUIT argentino
 * Formato: 20-12345678-9
 * @param {string} cuit - CUIT a validar
 * @returns {boolean} true si es válido
 */
export const isValidCUIT = (cuit) => {
  if (!cuit) return false;

  // Remover guiones y espacios
  const cleaned = cuit.replace(/[-\s]/g, '');

  // Debe tener 11 dígitos
  if (!/^\d{11}$/.test(cleaned)) {
    return false;
  }

  // Validar dígito verificador
  const multiplicadores = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
  let suma = 0;

  for (let i = 0; i < 10; i++) {
    suma += parseInt(cleaned[i]) * multiplicadores[i];
  }

  const resto = suma % 11;
  const digitoVerificador = resto === 0 ? 0 : resto === 1 ? 9 : 11 - resto;

  return digitoVerificador === parseInt(cleaned[10]);
};

/**
 * Validar número de cheque
 * @param {string} number - Número de cheque
 * @returns {boolean} true si es válido
 */
export const isValidChequeNumber = (number) => {
  if (!number) return false;

  // Número de cheque debe contener solo números y guiones
  // Longitud entre 6 y 20 caracteres
  const cleaned = String(number).replace(/[-\s]/g, '');

  if (!/^\d+$/.test(cleaned)) {
    return false;
  }

  if (cleaned.length < 6 || cleaned.length > 20) {
    return false;
  }

  return true;
};

/**
 * Validar monto de moneda
 * @param {number|string} value - Valor a validar
 * @returns {boolean} true si es válido
 */
export const isValidCurrency = (value) => {
  if (value === null || value === undefined || value === '') {
    return false;
  }

  const num = parseFloat(value);

  if (isNaN(num)) {
    return false;
  }

  // Debe ser mayor que 0 y tener máximo 2 decimales
  if (num <= 0) {
    return false;
  }

  const decimals = (value.toString().split('.')[1] || '').length;
  if (decimals > 2) {
    return false;
  }

  return true;
};

/**
 * Validar número de teléfono venezolano
 * @param {string} phone - Teléfono a validar
 * @returns {boolean} true si es válido
 */
export const isValidPhone = (phone) => {
  if (!phone) return false;

  // Remover caracteres no numéricos
  const cleaned = phone.replace(/\D/g, '');

  // Formatos válidos:
  // 0414-1234567 (11 dígitos)
  // 0212-1234567 (11 dígitos)
  // 414-1234567 (10 dígitos)
  return cleaned.length === 10 || cleaned.length === 11;
};

/**
 * Validar contraseña segura
 * Mínimo 6 caracteres
 * @param {string} password - Contraseña a validar
 * @returns {boolean} true si es válida
 */
export const isValidPassword = (password) => {
  if (!password) return false;

  // Mínimo 6 caracteres
  if (password.length < 6) {
    return false;
  }

  return true;
};

/**
 * Validar contraseña fuerte
 * Mínimo 8 caracteres, al menos una mayúscula, una minúscula y un número
 * @param {string} password - Contraseña a validar
 * @returns {boolean} true si es válida
 */
export const isStrongPassword = (password) => {
  if (!password) return false;

  // Mínimo 8 caracteres
  if (password.length < 8) {
    return false;
  }

  // Al menos una mayúscula
  if (!/[A-Z]/.test(password)) {
    return false;
  }

  // Al menos una minúscula
  if (!/[a-z]/.test(password)) {
    return false;
  }

  // Al menos un número
  if (!/[0-9]/.test(password)) {
    return false;
  }

  return true;
};

/**
 * Validar código postal
 * @param {string} zipCode - Código postal
 * @returns {boolean} true si es válido
 */
export const isValidZipCode = (zipCode) => {
  if (!zipCode) return false;

  // Formato: 1234 o 1234-AB
  const zipRegex = /^\d{4}(-[A-Z]{2})?$/i;
  return zipRegex.test(zipCode);
};

/**
 * Validar URL
 * @param {string} url - URL a validar
 * @returns {boolean} true si es válida
 */
export const isValidURL = (url) => {
  if (!url) return false;

  try {
    new URL(url);
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Validar fecha
 * @param {Date|string} date - Fecha a validar
 * @returns {boolean} true si es válida
 */
export const isValidDate = (date) => {
  if (!date) return false;

  const dateObj = new Date(date);
  return dateObj instanceof Date && !isNaN(dateObj);
};

/**
 * Validar que una fecha sea futura
 * @param {Date|string} date - Fecha a validar
 * @returns {boolean} true si es futura
 */
export const isFutureDate = (date) => {
  if (!isValidDate(date)) return false;

  const dateObj = new Date(date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return dateObj > today;
};

/**
 * Validar que una fecha sea pasada
 * @param {Date|string} date - Fecha a validar
 * @returns {boolean} true si es pasada
 */
export const isPastDate = (date) => {
  if (!isValidDate(date)) return false;

  const dateObj = new Date(date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return dateObj < today;
};

/**
 * Validar número entero positivo
 * @param {number|string} value - Valor a validar
 * @returns {boolean} true si es válido
 */
export const isPositiveInteger = (value) => {
  if (value === null || value === undefined || value === '') {
    return false;
  }

  const num = parseInt(value);
  return !isNaN(num) && num > 0 && num === parseFloat(value);
};

export default {
  isValidEmail,
  isValidRUC,
  isValidCUIT,
  isValidChequeNumber,
  isValidCurrency,
  isValidPhone,
  isValidPassword,
  isStrongPassword,
  isValidZipCode,
  isValidURL,
  isValidDate,
  isFutureDate,
  isPastDate,
  isPositiveInteger,
};
